import { useEffect, useState } from 'react'
import { useReducedMotion } from './useReducedMotion'

/**
 * Keep an overlay mounted while it animates out (UX-117: hard-cut exits).
 *
 *   const { mounted, leaving } = usePresence(isOpen, 160)
 *   if (!mounted) return null
 *   <div className={leaving ? 'animate-overlay-out' : 'animate-overlay-in'} />
 *
 * `leaving` flips in the same render that `open` goes false (derived state,
 * not an effect), so there is never a frame where the overlay is gone and
 * then comes back to animate out. Under reduced motion it unmounts at once.
 */
export function usePresence(open: boolean, exitMs: number): { mounted: boolean; leaving: boolean } {
  const reduced = useReducedMotion()
  const [leaving, setLeaving] = useState(false)
  const [prev, setPrev] = useState(open)
  if (open !== prev) {
    setPrev(open)
    setLeaving(!open && !reduced && exitMs > 0)
  }
  useEffect(() => {
    if (!leaving) return
    const t = window.setTimeout(() => setLeaving(false), exitMs)
    return () => window.clearTimeout(t)
  }, [leaving, exitMs])
  return { mounted: open || leaving, leaving }
}
